import { Checkbox, Divider, Text, useBoolean, VStack } from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useOutletContext } from 'react-router-dom';

import { AppError } from '../../../framework/models/errors';
import { TypeUtils } from '../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../components/Check';
import DateMatter from '../../components/DateMatter';
import FormFiles from '../../components/form/FormFiles';
import { SaveButton } from '../../components/StyledButton';
import { pagePaths } from '../../constants/Page';
import { useGlobalStateMachine } from '../../GlobalStore';
import { useFormItems } from '../../hooks/formItems';
import { useEngeneNavigate } from '../../hooks/navigate';
import { usePostAttachedFilePreview } from '../../hooks/queries/attachedFile';
import { usePostOutsourceMemberReportWorkDocReport } from '../../hooks/queries/outsourceMemberReport';
import useToastHook from '../../hooks/ToastHook';
import { EngeneFileObject } from '../../models/EngeneFileObject';
import { DailyMatterReportSaveParam } from '../../models/gen/dailyMatterReportSaveParam';
import { FileKind } from '../../models/gen/fileKind';
import { OutsourceMemberReportWorkDocReportSaveParam } from '../../models/gen/outsourceMemberReportWorkDocReportSaveParam';
import { FileUtils } from '../../utils/FileUtils';
import { useSizeContext } from '../SizeProvider';
import { OutsourceMemberReportOutletContext } from './Root';

export type ReportState = DailyMatterReportSaveParam & {
  /**
   * 作業報告書提出書
   */
  reports?: Array<EngeneFileObject>;
};

/**
 *
 * @returns
 */
const WorkDocReport: React.FunctionComponent<{ mode: 'edit' | 'view' }> = (
  props
) => {
  const { actions } = useGlobalStateMachine();
  const { authCode, workDocReport, isLoading, facilitySettings } =
    useOutletContext<OutsourceMemberReportOutletContext>();
  // if (TypeUtils.isNil(authCode)) {
  //   return <Navigate to="../error403" />;
  // } else if (TypeUtils.isNil(workDocReport)) {
  //   return <Navigate to={`../error404?authCode=${authCode}`} />;
  // }
  // TODO: 提出済ならリダイレクト
  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();

  const { CheckArea, checked, off: offCheck } = useCheckArea();
  const form = useForm<ReportState>();
  const { FormTextarea } = useFormItems(form);

  const [visibleCheckArea, { on, off }] = useBoolean(
    props.mode === 'edit' && (form.getValues('reports') || []).length > 0
  );

  const [checked1, { toggle: toggle1, off: off1 }] = useBoolean();
  const [checked2, { toggle: toggle2, off: off2 }] = useBoolean();
  const [checked3, { toggle: toggle3, off: off3 }] = useBoolean();

  React.useEffect(() => {
    if (visibleCheckArea) {
      off1();
      off2();
      off3();
      offCheck();
    }
  }, [visibleCheckArea]);

  const saveWorkDocReport = usePostOutsourceMemberReportWorkDocReport();
  React.useEffect(() => {
    if (!isLoading) {
      const attachFiles = workDocReport?.dailyMatterReport?.attachFiles;
      if (Array.isArray(attachFiles) && attachFiles.length > 0) {
        form.reset({
          dailyMatterId: workDocReport?.dailyMatterReport?.dailyMatterId,
          dailyMatterVersion:
            workDocReport?.dailyMatterReport?.dailyMatterVersion,
          reportDocMemo: workDocReport?.dailyMatterReport?.reportDocMemo,
          reports: attachFiles
            .filter(
              (attachFile) =>
                TypeUtils.isNonNilNumber(attachFile.id) &&
                TypeUtils.isNonNilString(attachFile.fileName)
            )
            .map(
              (attachFile) =>
                new EngeneFileObject(
                  attachFile.fileName!,
                  attachFile.id,
                  attachFile.fileSize
                )
            ),
        });
      } else {
        form.reset({
          dailyMatterId: workDocReport?.dailyMatterReport?.dailyMatterId,
          dailyMatterVersion:
            workDocReport?.dailyMatterReport?.dailyMatterVersion,
          reportDocMemo: workDocReport?.dailyMatterReport?.reportDocMemo,
        });
      }
    }
  }, [isLoading, workDocReport]);
  const { toast } = useToastHook();

  const handleSubmit = (fieldValues: ReportState) => {
    const formData = new FormData();
    const saveFileNames: string[] = [];
    fieldValues.reports
      ?.filter((report) => TypeUtils.isNonNilString(report.newFileName))
      ?.filter(
        (report) =>
          !workDocReport?.dailyMatterReport?.attachFiles
            ?.map((attachedFile) => attachedFile.fileName)
            .includes(report.newFileName)
      )
      .forEach((report) => saveFileNames.push(report.newFileName!));

    const metadata: OutsourceMemberReportWorkDocReportSaveParam = {
      authCode,
      workDocReport: {
        dailyMatterId: workDocReport?.dailyMatterReport?.dailyMatterId,
        dailyMatterVersion:
          workDocReport?.dailyMatterReport?.dailyMatterVersion,
        reportDocMemo: fieldValues.reportDocMemo,
        deleteFileNames: fieldValues.deleteFileNames || [],
        saveFileNames: saveFileNames,
      },
    };
    formData.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );

    const validate = FileUtils.validate(
      fieldValues.reports,
      facilitySettings,
      FileKind.ReportDoc
    );
    if (TypeUtils.isNonNilString(validate)) {
      toast(validate, 'warning');
      return;
    }

    fieldValues.reports
      ?.filter(
        (report) =>
          !workDocReport?.dailyMatterReport?.attachFiles
            ?.map((attachedFile) => attachedFile.fileName)
            .includes(report.newFileName || report.fileName) &&
          TypeUtils.isNonNil(report.file)
      )
      .forEach((report) =>
        formData.append('files', report.file!, report.fileName)
      );

    actions.setGuard(true);
    saveWorkDocReport.mutate(formData, {
      onSuccess: () => {
        // toast('作業報告書を提出しました');
        navigate(pagePaths.SUCCEEDED);
        actions.setGuard(false);
      },
      onError: (error) => {
        toast(
          error instanceof AppError
            ? error
            : '正常に処理を完了できませんでした．代表者に報告して下さい'
        );
        actions.setGuard(false);
      },
    });
  };

  const onDeleteFile = React.useCallback<Consumer<string>>(
    (filename) => {
      const deleteFileNames = form.getValues('deleteFileNames') || [];
      if (
        Array.isArray(deleteFileNames) &&
        !deleteFileNames.includes(filename)
      ) {
        deleteFileNames.push(filename);
      }
      form.setValue('deleteFileNames', deleteFileNames);
    },
    [form]
  );
  const onChangeFile = React.useCallback<Consumer<unknown[]>>(
    (files) => {
      if (props.mode === 'edit' && files.length > 0) on();
      else off();
    },
    [props.mode]
  );

  return (
    <VStack
      alignItems="start"
      as="form"
      fontSize={sizes.fontSize.rawMessage}
      onSubmit={form?.handleSubmit(handleSubmit)}
      width={sizes.width.styledMessageList}
    >
      <DateMatter
        customerName={workDocReport?.matterSummary?.customerName}
        dateString={workDocReport?.dailyMatterReport?.date}
        matterName={workDocReport?.matterSummary?.matterName}
      />
      <VStack
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        padding={2}
        width="full"
      >
        {props.mode === 'edit' ? (
          <FormFiles
            form={form}
            mode={props.mode}
            name="reports"
            onDelete={onDeleteFile}
            onChange={onChangeFile}
            prefix="作業報告書"
            startIndex={FileUtils.getNextIndex(
              workDocReport?.dailyMatterReport?.attachFiles?.map(
                (attachedFile) => attachedFile.fileName
              ) || []
            )}
            visibleFilename
          />
        ) : null}
        <FormTextarea label="備考" mode={props.mode} name="reportDocMemo" />
      </VStack>
      <CheckArea
        additionalArea={
          <>
            <VStack fontSize={sizes.fontSize.listMessage}>
              <Text color="red" fontWeight="bold">
                !!! 注意 !!!
                一度確定すると原則訂正できません．報告書画像について、以下をチェックしてください
              </Text>
              <VStack
                alignItems="left"
                fontSize={sizes.fontSize.confirmMessage}
              >
                <Checkbox isChecked={checked1} onChange={toggle1}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    画像が鮮明であること
                  </Text>
                </Checkbox>
                <Checkbox isChecked={checked2} onChange={toggle2}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    報告書全体が写っていること
                  </Text>
                </Checkbox>
                <Checkbox isChecked={checked3} onChange={toggle3}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    手ブレしていないこと
                  </Text>
                </Checkbox>
              </VStack>
            </VStack>
            <Divider />
          </>
        }
        button={SaveButton}
        buttonProps={{
          isDisabled: !checked || !checked1 || !checked2 || !checked3,
        }}
        confirmMessage="上記の通りの作業内容であることを確認しました"
        hidden={
          !visibleCheckArea
          // props.mode === 'view' || form.getValues('reports')?.length === 0
          //  ||
          // (Array.isArray(data?.dailyMatterReport?.attachFiles) &&
          //   data!.dailyMatterReport!.attachFiles!.length > 0)
        }
      />
    </VStack>
  );
};

export default WorkDocReport;
